import React, { FunctionComponent, useRef, useState, useEffect } from 'react'
import { PaymentType } from '@afterpay/types'
import { Heading, Radio, Text, useIsDesignLanguage, DesignLanguage } from '@afterpaytouch/core'
import {
  setSelectedPaymentType,
  useAppDispatch,
  useCheckoutState,
  useExperiment,
  useIsAfterpayFinanceFeeEligible,
  usePBIRejected,
  usePaymentOptionsError,
  usePreselectedPaymentType,
  useSelectedPaymentType,
} from '../../../state'
import clsx from 'clsx'
import styles from './style.module.css'
import { ConsumerLendingTagline } from '../../ConsumerLendingTagline'
import { ConsumerLendingRadioLabel } from './ConsumerLendingRadioLabel'
import { useCheckoutSizes } from '../../../utils/responsive'
import { PayInFourRadioLabel } from './PayInFourRadioLabel'
import { useTranslation } from '../../../utils/convergence'
import { PCLTooltip } from './PCLTooltip'

export const PaymentTypesRadioGroup: FunctionComponent = () => {
  const isArcadeDesignLanguage = useIsDesignLanguage(DesignLanguage.Arcade)
  const { t } = useTranslation()
  const selectedPaymentType = useSelectedPaymentType()
  const { started } = useCheckoutState()
  const installmentsCount = started?.paymentSchedules?.length ?? 4
  const dispatch = useAppDispatch()
  const { extraSmallTextSize, textSize } = useCheckoutSizes()
  const hasPBIRejected = usePBIRejected()
  const paymentOptionsError = usePaymentOptionsError()
  const isAFF = useIsAfterpayFinanceFeeEligible()
  const buttonRef = useRef(null)
  const id = 'payInMoreAPR-help-tooltip'

  const [showTooltip, setShowTooltip] = useState(false)
  const [hasClickedOutside, setHasClickedOutside] = useState<boolean>(false)
  const preselectedPaymentType = usePreselectedPaymentType()

  const payInFourHeadingStyles = clsx(styles.payInFourHeading, hasPBIRejected && styles.PBIRejectedGray)
  const { active: isDynamicAprLabelActive } = useExperiment('ConsumerLendingEnableDynamicAprLabel')

  const clickPaymentTypesGroup = (e): void => {
    const element = e.currentTarget as HTMLInputElement
    dispatch(setSelectedPaymentType(element.value as PaymentType))
  }

  useEffect(() => {
    if (preselectedPaymentType) {
      dispatch(setSelectedPaymentType(preselectedPaymentType))
    }
  }, [preselectedPaymentType])

  return (
    <Radio.Group id='payment-types' name='payment-types' value={selectedPaymentType?.toString()} testNameSpace='payment-types' onClick={clickPaymentTypesGroup}>
      <div className={styles.payInMoreTagline}>
        <ConsumerLendingTagline />
      </div>

      <div className={styles.eligibilityHeading}>
        <Text size='XS' color='Gray50'>
          {t('consumerLending:paymentOptions.checkYourEligibility')}
        </Text>
      </div>

      {/* Consumer lending */}
      <Radio.Button
        value={PaymentType.PCL}
        defaultChecked={selectedPaymentType === PaymentType.PCL}
        selectedBorder={selectedPaymentType === PaymentType.PCL && !isArcadeDesignLanguage}
        labelPlacement='Top'
        kind='Option'
        labelWidth='Full'
        roundedBorder
        reversed
        disabled={Boolean(paymentOptionsError?.error)}
        label={() => (
          <ConsumerLendingRadioLabel
            buttonRef={buttonRef}
            showTooltip={showTooltip}
            setShowTooltip={setShowTooltip}
            hasClickedOutside={hasClickedOutside}
            setHasClickedOutside={setHasClickedOutside}
            payInMoreAPRAriaDescribedBy={id}
          />
        )}
        id='payment-types-pcl'
        testNameSpace='payment-types'
        name='payment-types'
      />
      {showTooltip && <PCLTooltip tooltipId={id} buttonRef={buttonRef} setHasClickedOutside={setHasClickedOutside} onClose={() => setShowTooltip(false)} />}

      <div className={payInFourHeadingStyles}>
        <Heading size={textSize}>{t('consumerLending:payInFourTagline', { context: isAFF ? 'aff' : '', interval: installmentsCount })}</Heading>
      </div>

      {/* Pay in 4 */}
      <Radio.Button
        value={PaymentType.PBI}
        selectedBorder={selectedPaymentType === PaymentType.PBI && !isArcadeDesignLanguage}
        defaultChecked={selectedPaymentType === PaymentType.PBI}
        labelPlacement='Top'
        kind='Option'
        disabled={hasPBIRejected}
        roundedBorder
        reversed
        label={() => <PayInFourRadioLabel />}
        id='payment-types-pbi'
      />

      <div className={styles.payInMoreSubjectToChange}>
        <Text color='Gray50' size={isArcadeDesignLanguage ? 'XXS' : extraSmallTextSize} fontFace='CashSansMono'>
          {t('consumerLending:paymentOptions.subjectToChange', { context: isDynamicAprLabelActive ? 'dynamic' : '' })}
        </Text>
      </div>
    </Radio.Group>
  )
}
